import { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface PinSetupModalProps {
  open: boolean;
  onComplete: () => void;
  onSetPin: (pin: string) => void;
}

type Step = "enter" | "confirm";

const NUMPAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];

export function PinSetupModal({ open, onComplete, onSetPin }: PinSetupModalProps) {
  const [step, setStep] = useState<Step>("enter");
  const [firstPin, setFirstPin] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setStep("enter");
      setFirstPin("");
      setCurrentPin("");
      setError("");
      setShake(false);
    }
  }, [open]);

  function handleKey(key: string) {
    if (key === "⌫") {
      setCurrentPin((p) => p.slice(0, -1));
      setError("");
      return;
    }
    if (key === "") return;
    if (currentPin.length >= 4) return;

    const next = currentPin + key;
    setCurrentPin(next);

    if (next.length === 4) {
      if (step === "enter") {
        setFirstPin(next);
        setCurrentPin("");
        setStep("confirm");
      } else {
        if (next === firstPin) {
          onSetPin(next);
          onComplete();
        } else {
          triggerShake();
          setCurrentPin("");
          setError("PINs don't match — try again");
        }
      }
    }
  }

  function triggerShake() {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  }

  const dots = Array.from({ length: 4 }, (_, i) => i < currentPin.length);

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-xs"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <DialogTitle className="text-xl">
            {step === "enter" ? "Set Kid Mode PIN" : "Confirm your PIN"}
          </DialogTitle>
          <DialogDescription>
            {step === "enter"
              ? "Choose a 4-digit PIN. This PIN unlocks all your players' Kid Mode."
              : "Enter the same PIN again to confirm."}
          </DialogDescription>
        </DialogHeader>

        {/* Dot indicators */}
        <div className={`flex justify-center gap-4 py-2 ${shake ? "animate-shake" : ""}`}>
          {dots.map((filled, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-colors ${
                filled
                  ? "bg-emerald-500 border-emerald-500"
                  : "border-muted-foreground"
              }`}
            />
          ))}
        </div>

        {error && (
          <p className="text-center text-sm text-destructive">{error}</p>
        )}

        {/* Numpad */}
        <div className="grid grid-cols-3 gap-2 pt-2">
          {NUMPAD.map((key, i) => (
            <button
              key={i}
              onClick={() => handleKey(key)}
              disabled={key === ""}
              className={`h-14 rounded-xl text-xl font-semibold transition-colors ${
                key === ""
                  ? "invisible"
                  : key === "⌫"
                  ? "bg-muted hover:bg-muted/80 text-muted-foreground"
                  : "bg-muted hover:bg-accent active:scale-95"
              }`}
            >
              {key}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
